import React, { useCallback, useMemo, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { ruRU } from "@mui/x-data-grid/locales";
import {
  Alert,
  AppBar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import Slide from "@mui/material/Slide";
import CloudDownloadRoundedIcon from "@mui/icons-material/CloudDownloadRounded";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

import { useUnit } from "effector-react";
import { bulkApi } from "../api/bulk";
import ConfirmDialog from "../components/ConfirmModal";
import { downloadBlobAsFile } from "../utils/download";
import { showSoftSuccess } from "../utils/softSuccess";
import { $isSettlementTenant } from "../store/auth";
import { getObjectKindLabels } from "../utils/complexKind";

const REQUIRED_FIELDS = [
  { key: "rc_name", label: "ЖК Название" },
  { key: "building_name", label: "Дом Название" },
  { key: "apartment_number", label: "Квартира Номер" },
];

const SETTLEMENT_HIDDEN_FIELDS = new Set([
  "corps_name",
  "apartment_number",
  "apartment_suffix",
  "apartment_floor",
  "apartment_section",
  "apartment_area",
  "apartment_owner",
  "apartment_heat_room_coefficient",
  "apartment_heat_calculate_by_area",
]);

const DEVICE_FIELDS = [
  "device_name",
  "device_system_type",
  "device_manufacturer",
  "device_serial",
  "device_dev_eui",
  "device_mqtt_topic",
  "device_reading_collection_mode",
  "device_heat_coefficient",
  "radiator_model_name",
  "radiator_manufacturer",
  "radiator_placement",
  "radiator_room_label",
  "radiator_section_count",
  "radiator_power_watts",
  "radiator_dimensions_mm",
  "radiator_height_mm",
  "radiator_width_mm",
  "radiator_depth_mm",
  "radiator_is_corner",
  "radiator_notes",
  "pulse_registrar_key",
  "pulse_registrar_name",
  "pulse_registrar_manufacturer",
  "pulse_registrar_model",
  "pulse_registrar_transport",
  "pulse_registrar_dev_eui",
  "pulse_registrar_mqtt_topic",
  "pulse_registrar_channel_count",
  "pulse_channel_number",
  "pulse_weight",
  "pulse_weight_mode",
  "pulse_weight_unit",
  "pulse_baseline_count",
  "pulse_baseline_meter",
  "pulse_coefficient_source",
  "uspd_name",
  "uspd_line_number",
  "uspd_transport",
  "uspd_bus_address",
  "uspd_profile_key",
  "radio_head_serial",
  "radio_head_dev_eui",
  "radio_head_manufacturer",
  "radio_head_model",
  "radio_head_baseline_meter",
];

const COLUMN_CONFIG = [
  { key: "rc_name", label: "ЖК Название", required: true },
  { key: "rc_address", label: "ЖК Адрес" },
  { key: "building_name", label: "Дом Название", required: true },
  { key: "building_address", label: "Дом Адрес" },
  { key: "corps_name", label: "Корпус Название" },
  { key: "apartment_number", label: "Квартира Номер", required: true },
  { key: "apartment_suffix", label: "Квартира Суффикс" },
  { key: "apartment_floor", label: "Квартира Этаж" },
  { key: "apartment_section", label: "Квартира Секция" },
  { key: "apartment_area", label: "Квартира Площадь" },
  { key: "apartment_owner", label: "Квартира Владелец" },
  { key: "device_name", label: "Устройство Наименование" },
  { key: "device_system_type", label: "Устройство Тип системы" },
  { key: "device_manufacturer", label: "Устройство Производитель" },
  { key: "device_serial", label: "Устройство Серийный номер" },
  { key: "device_dev_eui", label: "Устройство DevEUI" },
  { key: "device_mqtt_topic", label: "Устройство MQTT-топик" },
  { key: "device_reading_collection_mode", label: "Устройство Режим сбора" },
  { key: "radiator_model_name", label: "УРТ Модель радиатора" },
  { key: "radiator_manufacturer", label: "УРТ Производитель" },
  { key: "radiator_placement", label: "УРТ Расположение" },
  { key: "radiator_room_label", label: "УРТ Помещение/метка" },
  { key: "radiator_section_count", label: "УРТ Кол-во секций" },
  { key: "radiator_power_watts", label: "УРТ Мощность, Вт" },
  { key: "apartment_heat_room_coefficient", label: "Квартира Понижающий коэффициент" },
  { key: "apartment_heat_calculate_by_area", label: "Квартира считать по площади (да/нет)" },
  {
    key: "radiator_dimensions_mm",
    label: "УРТ Габаритные размеры (мм) Длина * Выс * Шир",
  },
  { key: "radiator_height_mm", label: "УРТ Высота, мм" },
  { key: "radiator_width_mm", label: "УРТ Ширина, мм" },
  { key: "radiator_depth_mm", label: "УРТ Глубина, мм" },
  { key: "radiator_is_corner", label: "УРТ Угловой (да/нет)" },
  { key: "radiator_notes", label: "УРТ Примечание" },
  { key: "device_heat_coefficient", label: "УРТ Расчётный коэффициент" },
  { key: "pulse_registrar_key", label: "Регистратор Ключ" },
  { key: "pulse_registrar_name", label: "Регистратор Название" },
  { key: "pulse_registrar_manufacturer", label: "Регистратор Производитель" },
  { key: "pulse_registrar_model", label: "Регистратор Модель" },
  { key: "pulse_registrar_transport", label: "Регистратор Транспорт" },
  { key: "pulse_registrar_dev_eui", label: "Регистратор DevEUI" },
  { key: "pulse_registrar_mqtt_topic", label: "Регистратор MQTT-топик" },
  { key: "pulse_registrar_channel_count", label: "Регистратор Кол-во каналов" },
  { key: "pulse_channel_number", label: "Регистратор № канала" },
  { key: "pulse_weight", label: "Импульс Вес" },
  { key: "pulse_weight_mode", label: "Импульс Формат веса" },
  { key: "pulse_weight_unit", label: "Импульс Единица" },
  { key: "pulse_baseline_count", label: "Импульс Начальное значение канала" },
  { key: "pulse_baseline_meter", label: "Импульс Начальное показание счётчика" },
  { key: "pulse_coefficient_source", label: "Импульс Источник коэффициента" },
  { key: "uspd_name", label: "УСПД Название" },
  { key: "uspd_line_number", label: "УСПД № линии" },
  { key: "uspd_transport", label: "УСПД Тип шины" },
  { key: "uspd_bus_address", label: "УСПД Адрес на шине" },
  { key: "uspd_profile_key", label: "УСПД Профиль прибора" },
  { key: "radio_head_serial", label: "Накладка Серийный номер" },
  { key: "radio_head_dev_eui", label: "Накладка DevEUI" },
  { key: "radio_head_manufacturer", label: "Накладка Производитель" },
  { key: "radio_head_model", label: "Накладка Модель" },
  {
    key: "radio_head_baseline_meter",
    label: "Накладка Начальное показание счётчика",
  },
];

const FIELD_BY_LABEL = COLUMN_CONFIG.reduce((acc, column) => {
  acc[column.label] = column.key;
  // Settlement aliases (same Excel keys, different UI wording)
  if (column.key === "rc_name") acc["Посёлок Название"] = column.key;
  if (column.key === "rc_address") acc["Посёлок Адрес"] = column.key;
  if (column.key === "apartment_number") acc["Участок Номер"] = column.key;
  return acc;
}, {});

const EMPTY_PREVIEW_ROW = COLUMN_CONFIG.reduce((acc, column) => {
  acc[column.key] = "";
  return acc;
}, {});

const ERROR_FIELD_ALIASES = [
  { pattern: /devEUI/i, keys: ["device_dev_eui", "pulse_registrar_dev_eui", "radio_head_dev_eui"] },
  { pattern: /накладк/i, keys: ["radio_head_serial", "radio_head_dev_eui", "radio_head_baseline_meter"] },
  { pattern: /MQTT/i, keys: ["device_mqtt_topic", "pulse_registrar_mqtt_topic"] },
  { pattern: /тип системы/i, keys: ["device_system_type"] },
  { pattern: /серийный номер/i, keys: ["device_serial"] },
  { pattern: /параметры УРТ/i, keys: ["device_system_type"] },
  { pattern: /регистратор/i, keys: ["pulse_registrar_key", "pulse_channel_number"] },
  { pattern: /импульс/i, keys: ["pulse_weight", "pulse_weight_mode", "pulse_weight_unit"] },
  { pattern: /профиль прибора|вторичн.*адресац/i, keys: ["uspd_profile_key"] },
  { pattern: /адрес.*шин|M-Bus ID/i, keys: ["uspd_bus_address"] },
];

const PreviewDialogTransition = React.forwardRef(
  function PreviewDialogTransition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
  }
);

const getTextValue = (value) => {
  if (value === null || value === undefined) {
    return "";
  }
  return String(value);
};

const toEditableRow = (row, index) => {
  const editable = { ...row };
  COLUMN_CONFIG.forEach((column) => {
    editable[column.key] = getTextValue(row[column.key]);
  });
  editable.row_index = row.row_index ?? index + 2;
  editable.device_present = DEVICE_FIELDS.some(
    (key) => getTextValue(row[key]).trim().length > 0
  );
  editable.server_row_errors = Array.isArray(row.row_errors)
    ? [...row.row_errors]
    : [];
  editable.server_row_warnings = Array.isArray(row.row_warnings)
    ? [...row.row_warnings]
    : [];
  return editable;
};

const getErrorFieldKeys = (errors) => {
  const keys = new Set();
  errors.forEach((message) => {
    const text = String(message || "");
    const quotedLabels = [...text.matchAll(/"([^"]+)"/g)].map((match) => match[1]);
    quotedLabels.forEach((label) => {
      if (FIELD_BY_LABEL[label]) {
        keys.add(FIELD_BY_LABEL[label]);
      }
    });
    COLUMN_CONFIG.forEach((column) => {
      if (text.includes(column.label)) {
        keys.add(column.key);
      }
    });
    ERROR_FIELD_ALIASES.forEach(({ pattern, keys: aliasKeys }) => {
      if (pattern.test(text)) {
        aliasKeys.forEach((key) => keys.add(key));
      }
    });
  });
  return Array.from(keys);
};

const sanitizeForImport = (rows) =>
  rows.map((row, index) => {
    const cleaned = {};
    COLUMN_CONFIG.forEach((column) => {
      const value = getTextValue(row[column.key]).trim();
      cleaned[column.key] = value.length ? value : null;
    });

    const devicePresent = DEVICE_FIELDS.some((key) => {
      const value = cleaned[key];
      return (
        value !== null && value !== undefined && String(value).trim().length > 0
      );
    });

    return {
      rc_name: cleaned.rc_name || "",
      building_name: cleaned.building_name || "",
      corps_name: cleaned.corps_name || "",
      apartment_number: cleaned.apartment_number || "",
      rc_address: cleaned.rc_address ?? undefined,
      building_address: cleaned.building_address ?? undefined,
      apartment_suffix: cleaned.apartment_suffix ?? undefined,
      apartment_floor: cleaned.apartment_floor ?? undefined,
      apartment_section: cleaned.apartment_section ?? undefined,
      apartment_area: cleaned.apartment_area ?? undefined,
      apartment_owner: cleaned.apartment_owner ?? undefined,
      apartment_heat_room_coefficient:
        cleaned.apartment_heat_room_coefficient ?? undefined,
      apartment_heat_calculate_by_area:
        cleaned.apartment_heat_calculate_by_area ?? undefined,
      device_name: cleaned.device_name ?? undefined,
      device_system_type: cleaned.device_system_type ?? undefined,
      device_manufacturer: cleaned.device_manufacturer ?? undefined,
      device_serial: cleaned.device_serial ?? undefined,
      device_dev_eui: cleaned.device_dev_eui ?? undefined,
      device_mqtt_topic: cleaned.device_mqtt_topic ?? undefined,
      device_reading_collection_mode:
        cleaned.device_reading_collection_mode ?? undefined,
      device_reading_collection_mode_explicit:
        Boolean(row.device_reading_collection_mode_explicit),
      device_heat_coefficient: cleaned.device_heat_coefficient ?? undefined,
      radiator_model_name: cleaned.radiator_model_name ?? undefined,
      radiator_manufacturer: cleaned.radiator_manufacturer ?? undefined,
      radiator_placement: cleaned.radiator_placement ?? undefined,
      radiator_room_label: cleaned.radiator_room_label ?? undefined,
      radiator_section_count: cleaned.radiator_section_count ?? undefined,
      radiator_power_watts: cleaned.radiator_power_watts ?? undefined,
      radiator_dimensions_mm: cleaned.radiator_dimensions_mm ?? undefined,
      radiator_height_mm: cleaned.radiator_height_mm ?? undefined,
      radiator_width_mm: cleaned.radiator_width_mm ?? undefined,
      radiator_depth_mm: cleaned.radiator_depth_mm ?? undefined,
      radiator_is_corner: cleaned.radiator_is_corner ?? undefined,
      radiator_notes: cleaned.radiator_notes ?? undefined,
      pulse_registrar_key: cleaned.pulse_registrar_key ?? undefined,
      pulse_registrar_name: cleaned.pulse_registrar_name ?? undefined,
      pulse_registrar_manufacturer: cleaned.pulse_registrar_manufacturer ?? undefined,
      pulse_registrar_model: cleaned.pulse_registrar_model ?? undefined,
      pulse_registrar_transport: cleaned.pulse_registrar_transport ?? undefined,
      pulse_registrar_dev_eui: cleaned.pulse_registrar_dev_eui ?? undefined,
      pulse_registrar_mqtt_topic: cleaned.pulse_registrar_mqtt_topic ?? undefined,
      pulse_registrar_channel_count: cleaned.pulse_registrar_channel_count ?? undefined,
      pulse_channel_number: cleaned.pulse_channel_number ?? undefined,
      pulse_weight: cleaned.pulse_weight ?? undefined,
      pulse_weight_mode: cleaned.pulse_weight_mode ?? undefined,
      pulse_weight_unit: cleaned.pulse_weight_unit ?? undefined,
      pulse_baseline_count: cleaned.pulse_baseline_count ?? undefined,
      pulse_baseline_meter: cleaned.pulse_baseline_meter ?? undefined,
      pulse_coefficient_source: cleaned.pulse_coefficient_source ?? undefined,
      uspd_name: cleaned.uspd_name ?? undefined,
      uspd_line_number: cleaned.uspd_line_number ?? undefined,
      uspd_transport: cleaned.uspd_transport ?? undefined,
      uspd_bus_address: cleaned.uspd_bus_address ?? undefined,
      uspd_profile_key: cleaned.uspd_profile_key ?? undefined,
      radio_head_serial: cleaned.radio_head_serial ?? undefined,
      radio_head_dev_eui: cleaned.radio_head_dev_eui ?? undefined,
      radio_head_manufacturer: cleaned.radio_head_manufacturer ?? undefined,
      radio_head_model: cleaned.radio_head_model ?? undefined,
      radio_head_baseline_meter: cleaned.radio_head_baseline_meter ?? undefined,
      device_present: devicePresent,
      row_index: row.row_index ?? index + 1,
    };
  });

const recalcRowErrors = (row) => {
  const errors = [];
  const serverErrors = Array.isArray(row.server_row_errors)
    ? row.server_row_errors
    : Array.isArray(row.row_errors)
    ? row.row_errors
    : [];

  REQUIRED_FIELDS.filter(
    ({ key }) => !row.is_settlement_row || key !== "apartment_number"
  ).forEach(({ key, label }) => {
    if (!getTextValue(row[key]).trim()) {
      errors.push(`Заполните поле "${label}"`);
    }
  });

  const hasDeviceData = DEVICE_FIELDS.some(
    (key) => getTextValue(row[key]).trim().length > 0
  );
  if (hasDeviceData) {
    if (!getTextValue(row.device_system_type).trim()) {
      errors.push(
        'Заполните поле "Устройство Тип системы" для создания устройства.'
      );
    }
    if (!getTextValue(row.device_serial).trim()) {
      errors.push(
        'Заполните поле "Устройство Серийный номер" для создания устройства.'
      );
    }
  }

  const hasPulseData = [
    "pulse_registrar_key",
    "pulse_channel_number",
    "pulse_registrar_dev_eui",
    "pulse_weight",
  ].some((key) => getTextValue(row[key]).trim().length > 0);
  if (hasPulseData) {
    if (!getTextValue(row.pulse_registrar_key).trim()) {
      errors.push('Заполните поле "Регистратор Ключ" для импульсного подключения.');
    }
    if (!getTextValue(row.pulse_channel_number).trim()) {
      errors.push('Заполните поле "Регистратор № канала".');
    }
    if (!getTextValue(row.device_serial).trim()) {
      errors.push(
        "Для привязки к каналу регистратора нужен серийный номер счётчика."
      );
    }
  }

  const hasUspdData = [
    "uspd_name",
    "uspd_line_number",
    "uspd_transport",
    "uspd_bus_address",
    "uspd_profile_key",
  ].some((key) => getTextValue(row[key]).trim().length > 0);
  if (hasUspdData && hasPulseData) {
    errors.push(
      "Нельзя одновременно заполнять блок регистратора и блок УСПД."
    );
  }
  if (hasUspdData) {
    if (!getTextValue(row.uspd_name).trim()) {
      errors.push('Заполните поле "УСПД Название".');
    }
    if (!getTextValue(row.uspd_line_number).trim()) {
      errors.push('Заполните поле "УСПД № линии".');
    }
    if (!getTextValue(row.uspd_bus_address).trim()) {
      errors.push('Заполните поле "УСПД Адрес на шине".');
    }
    if (!getTextValue(row.device_serial).trim()) {
      errors.push("Для привязки к линии УСПД нужен серийный номер счётчика.");
    }
  }

  const mergedErrors = Array.from(new Set([...serverErrors, ...errors]));
  const fieldErrorKeys = getErrorFieldKeys(mergedErrors);
  const serverWarnings = Array.isArray(row.server_row_warnings)
    ? row.server_row_warnings
    : Array.isArray(row.row_warnings)
    ? row.row_warnings
    : [];

  return {
    ...row,
    device_present: hasDeviceData,
    row_errors: mergedErrors,
    row_warnings: Array.from(new Set(serverWarnings)),
    field_error_keys: fieldErrorKeys,
  };
};

const normalizePreviewRows = (rows, isSettlement = false) =>
  rows.map((row, index) =>
    recalcRowErrors({
      ...toEditableRow(row, index),
      is_settlement_row: isSettlement,
    })
  );

const extractResponseErrors = (err) => {
  const data = err?.response?.data;
  const status = err?.response?.status;
  const topLevelErrors = Array.isArray(data?.errors) ? data.errors : [];
  const nestedErrors =
    data?.detail && Array.isArray(data.detail.errors) ? data.detail.errors : [];
  const nestedMessage =
    data?.detail && typeof data.detail.message === "string"
      ? data.detail.message
      : "";
  const detailMessage =
    typeof data?.detail === "string" && data.detail.trim().length > 0
      ? data.detail
      : "";
  const fallbackMessage =
    typeof err?.message === "string" && err.message.trim().length > 0
      ? err.message
      : "";

  return {
    errors: topLevelErrors.length ? topLevelErrors : nestedErrors,
    message:
      (status === 413
        ? "Файл слишком большой для загрузки. Уменьшите файл или увеличьте лимит загрузки на сервере."
        : "") ||
      detailMessage ||
      nestedMessage ||
      topLevelErrors[0] ||
      nestedErrors[0] ||
      fallbackMessage ||
      "",
  };
};

const BulkImportPage = ({ showExportAction = true }) => {
  const isSettlementTenant = useUnit($isSettlementTenant);
  const labels = useMemo(
    () => getObjectKindLabels(isSettlementTenant),
    [isSettlementTenant]
  );
  const [file, setFile] = useState(null);
  const [downloadMessage, setDownloadMessage] = useState("");
  const [previewRows, setPreviewRows] = useState([]);
  const [previewErrors, setPreviewErrors] = useState([]);
  const [serverValidationErrors, setServerValidationErrors] = useState([]);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [confirmImportOpen, setConfirmImportOpen] = useState(false);
  const [confirmUploadOpen, setConfirmUploadOpen] = useState(false);
  const [isPreviewModalOpen, setPreviewModalOpen] = useState(false);
  const [skipInvalidRows, setSkipInvalidRows] = useState(false);
  const [rowFilter, setRowFilter] = useState("all"); // all | errors | warnings
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const isFilteredView = rowFilter !== "all";

  const gridColumns = useMemo(() => {
    return [
      { field: "row_index", headerName: "#", width: 80, sortable: false },
      ...COLUMN_CONFIG.filter(
        (column) => !isSettlementTenant || !SETTLEMENT_HIDDEN_FIELDS.has(column.key)
      ).map((c) => ({
        field: c.key,
        headerName:
          (isSettlementTenant && c.key === "rc_name"
            ? "Посёлок Название"
            : isSettlementTenant && c.key === "rc_address"
            ? "Посёлок Адрес"
            : c.label) + (c.required ? " *" : ""),
        flex: 1,
        minWidth: 160,
        editable: true,
        sortable: false,
      })),
      {
        field: "row_errors_summary",
        headerName: "Ошибки",
        minWidth: 320,
        flex: 1.2,
        sortable: false,
        editable: false,
        renderCell: (params) => {
          const errors = Array.isArray(params.row?.row_errors)
            ? params.row.row_errors
            : [];
          if (!errors.length) {
            return "";
          }
          return (
            <Box
              component="span"
              sx={{
                color: "#B42318",
                fontSize: 12,
                lineHeight: 1.25,
                whiteSpace: "normal",
              }}
            >
              {errors.join(" ")}
            </Box>
          );
        },
      },
      {
        field: "row_warnings_summary",
        headerName: "Предупреждения",
        minWidth: 320,
        flex: 1.1,
        sortable: false,
        editable: false,
        renderCell: (params) => {
          const warnings = Array.isArray(params.row?.row_warnings)
            ? params.row.row_warnings
            : [];
          if (!warnings.length) {
            return "";
          }
          return (
            <Box
              component="span"
              sx={{
                color: "#92400E",
                fontSize: 12,
                lineHeight: 1.25,
                whiteSpace: "normal",
              }}
            >
              {warnings.join(" ")}
            </Box>
          );
        },
      },
    ];
  }, [isSettlementTenant]);
  // применяется при завершении редактирования строки (Enter/выход из ячейки)
  const processRowUpdate = useCallback((newRow) => {
    const updated = recalcRowErrors(newRow);
    // записываем в состояние
    setPreviewRows((prev) =>
      prev.map((r) => (r.row_index === updated.row_index ? updated : r))
    );
    return updated;
  }, []);

  // показывать ошибки строк подсветкой (красным фоном) — опционально
  const getRowClassName = useCallback((params) => {
    const hasErrors =
      Array.isArray(params.row?.row_errors) && params.row.row_errors.length > 0;
    if (hasErrors) {
      return "row-has-errors";
    }
    const hasWarnings =
      Array.isArray(params.row?.row_warnings) &&
      params.row.row_warnings.length > 0;
    return hasWarnings ? "row-has-warnings" : "";
  }, []);

  const isSpreadsheetSelected = useMemo(() => {
    if (!file) {
      return false;
    }
    const lower = file.name.toLowerCase();
    return lower.endsWith(".xlsx") || lower.endsWith(".csv");
  }, [file]);

  const hasRows = previewRows.length > 0;
  const rowsWithErrorsCount = useMemo(
    () =>
      rowFilter === "errors"
        ? previewRows.length
        : previewRows.filter(
            (row) => Array.isArray(row.row_errors) && row.row_errors.length > 0
          ).length,
    [previewRows, rowFilter]
  );
  const rowsWithWarningsCount = useMemo(
    () =>
      rowFilter === "warnings"
        ? previewRows.length
        : previewRows.filter(
            (row) => Array.isArray(row.row_warnings) && row.row_warnings.length > 0
          ).length,
    [previewRows, rowFilter]
  );
  const pageFrom = page * pageSize + 1;
  const pageTo = Math.min(
    (page + 1) * pageSize,
    isFilteredView ? previewRows.length : totalRows || previewRows.length
  );
  const previewRangeText = hasRows
    ? rowFilter === "errors"
      ? `Ошибочные строки: ${previewRows.length}`
      : rowFilter === "warnings"
      ? `Строки с предупреждениями: ${previewRows.length}`
      : `Строки ${pageFrom}–${pageTo} из ${totalRows || previewRows.length}`
    : rowFilter === "errors"
    ? "Ошибочных строк нет"
    : rowFilter === "warnings"
    ? "Строк с предупреждениями нет"
    : "Строк нет";
  const totalPages = isFilteredView
    ? Math.max(1, Math.ceil(previewRows.length / pageSize) || 0)
    : totalRows
    ? Math.ceil(totalRows / pageSize)
    : hasRows
    ? 1
    : 0;

  const loadPage = useCallback(
    async (selectedFile, nextPage = page, size = pageSize, filter = rowFilter) => {
      if (!selectedFile) return;
      setPreviewLoading(true);
      try {
        const filtered = filter !== "all";
        const offset = filtered ? 0 : nextPage * size;
        const limit = filtered ? 0 : size;
        const preview = await bulkApi.previewFile(selectedFile, {
          offset,
          limit,
          only_errors: filter === "errors",
          only_warnings: filter === "warnings",
        });
        const normalizedRows = normalizePreviewRows(
          preview.rows || [],
          isSettlementTenant
        );
        setPreviewRows(normalizedRows);
        setPreviewErrors(preview.errors || []);
        setTotalRows(preview.total_rows ?? normalizedRows.length);
        setPage(filtered ? 0 : nextPage);
        setPageSize(size);
        // Не закрываем уже открытый предпросмотр (например, при фильтре без совпадений).
        setPreviewModalOpen((wasOpen) =>
          wasOpen ||
          normalizedRows.length > 0 ||
          (preview.errors || []).length > 0
        );
      } catch (err) {
        console.error(err);
        const { errors, message } = extractResponseErrors(err);
        if (errors.length) {
          setPreviewErrors(errors);
          setPreviewModalOpen(true);
        }
        setError(
          message ||
            "Не удалось построить предварительный просмотр."
        );
      } finally {
        setPreviewLoading(false);
      }
    },
    [isSettlementTenant, page, pageSize, rowFilter]
  );

  const handlePreviewOpen = () => setPreviewModalOpen(true);
  const handlePreviewClose = () => setPreviewModalOpen(false);

  const handleRowFilterChange = async (nextFilter) => {
    const filter = rowFilter === nextFilter ? "all" : nextFilter;
    setRowFilter(filter);
    setPage(0);
    if (file) {
      await loadPage(file, 0, pageSize, filter);
    }
  };

  const handleDownloadTemplate = async () => {
    setError("");
    setDownloadMessage("");
    try {
      const blob = await bulkApi.downloadTemplate();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "billingservice_objects_template.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setDownloadMessage("Шаблон сохранён");
      showSoftSuccess("Шаблон сохранён");
    } catch (err) {
      console.error(err);
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Не удалось скачать шаблон. Попробуйте позже."
      );
    }
  };

  const handleExportObjects = async () => {
    setError("");
    setDownloadMessage("");
    setExporting(true);
    try {
      const { blob, filename } = await bulkApi.exportObjects();
      downloadBlobAsFile(blob, filename);
      setDownloadMessage("Выгрузка готова");
      showSoftSuccess("Выгрузка готова");
    } catch (err) {
      console.error(err);
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Не удалось сформировать выгрузку. Попробуйте позже."
      );
    } finally {
      setExporting(false);
    }
  };

  const handleFileChange = async (event) => {
    const selected = event.target.files?.[0] || null;
    setFile(selected);
    setTotalRows(0);
    setPage(0);
    setSuccess(null);
    setError("");
    setDownloadMessage("");
    setServerValidationErrors([]);
    setPreviewRows([]);
    setPreviewErrors([]);
    setPreviewModalOpen(false);
    setRowFilter("all");

    if (!selected) {
      return;
    }

    if (
      !selected.name.toLowerCase().endsWith(".xlsx") &&
      !selected.name.toLowerCase().endsWith(".csv")
    ) {
      setError("Выберите файл формата .xlsx или .csv, полученный из шаблона.");
      return;
    }

    // setPreviewLoading(true);
    // try {
    //   const preview = await bulkApi.previewFile(selected);
    //   const normalizedRows = normalizePreviewRows(preview.rows || []);
    //   setPreviewRows(normalizedRows);
    //   setPreviewErrors(preview.errors || []);
    //   if (normalizedRows.length > 0) {
    //     setPreviewModalOpen(true);
    //   }
    // } catch (err) {
    //   console.error(err);
    //   const detail = err?.response?.data?.detail;
    //   if (detail?.errors && Array.isArray(detail.errors)) {
    //     setPreviewErrors(detail.errors);
    //   }
    //   setError(
    //     detail ||
    //       err?.message ||
    //       "Не удалось построить предварительный просмотр."
    //   );
    // } finally {
    //   setPreviewLoading(false);
    // }
    await loadPage(selected, 0, pageSize);
  };

  const handleRowChange = (index, key, value) => {
    setPreviewRows((prev) => {
      const next = [...prev];
      const updated = { ...next[index], [key]: value };
      next[index] = recalcRowErrors(updated);
      return next;
    });
  };

  const handleRemoveRow = (index) => {
    setPreviewRows((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddRow = () => {
    const nextIndex = previewRows.length
      ? Math.max(...previewRows.map((row) => row.row_index || 0)) + 1
      : 2;
    const emptyRow = recalcRowErrors({
      ...EMPTY_PREVIEW_ROW,
      is_settlement_row: isSettlementTenant,
      device_present: false,
      row_index: nextIndex,
      row_errors: [],
    });
    setPreviewRows((prev) => [...prev, emptyRow]);
  };

  const validateRowsBeforeImport = () => {
    const nextRows = previewRows.map((row) => recalcRowErrors(row));
    setPreviewRows(nextRows);
    const invalidCount = nextRows.filter(
      (row) => Array.isArray(row.row_errors) && row.row_errors.length > 0
    ).length;
    const validCount = nextRows.length - invalidCount;
    if (invalidCount > 0 && !skipInvalidRows) {
      setError(
        "Обнаружены ошибки в заполнении строк. Исправьте их перед импортом или включите пропуск ошибочных строк."
      );
      return false;
    }
    if (validCount <= 0) {
      setError("Нет корректных строк для импорта.");
      return false;
    }
    return true;
  };

  const handleUpload = async () => {
    if (!file || !isSpreadsheetSelected) {
      setError("Выберите файл формата .xlsx или .csv, полученный из шаблона.");
      return;
    }
    setUploading(true);
    setError("");
    setSuccess(null);
    setServerValidationErrors([]);
    try {
      const result = await bulkApi.uploadFile(file, {
        skip_invalid_rows: skipInvalidRows,
        acknowledge_skip_invalid: skipInvalidRows,
      });
      setSuccess(result);
      showSoftSuccess("Файл загружен");
    } catch (err) {
      console.error(err);
      const { errors, message } = extractResponseErrors(err);
      if (errors.length) {
        setServerValidationErrors(errors);
      }
      setError(message || "Не удалось импортировать файл.");
    } finally {
      setUploading(false);
    }
  };

  const handleImport = async () => {
    setError("");
    setServerValidationErrors([]);
    setSuccess(null);

    if (!validateRowsBeforeImport()) {
      return;
    }

    const payloadRows = sanitizeForImport(previewRows);

    setImporting(true);
    try {
      const result = await bulkApi.importRows(payloadRows, {
        skip_invalid_rows: skipInvalidRows,
        acknowledge_skip_invalid: skipInvalidRows,
      });
      setSuccess(result);
      showSoftSuccess("Импорт готов");
    } catch (err) {
      console.error(err);
      const { errors, message } = extractResponseErrors(err);
      if (errors.length) {
        setServerValidationErrors(errors);
      }
      setError(message || "Не удалось подтвердить импорт.");
    } finally {
      setImporting(false);
    }
  };

  const summaryInfo = useMemo(() => {
    if (!success) {
      return null;
    }
    const updated = success.updated || {};
    const updatedTotal = Object.values(updated).reduce(
      (sum, value) => sum + (Number(value) || 0),
      0
    );
    const created = success.created || {};
    const bits = [
      created.residential_complexes
        ? `${labels.complex} ${created.residential_complexes}`
        : null,
      created.buildings ? `домов ${created.buildings}` : null,
      !isSettlementTenant && created.building_corps
        ? `корпусов ${created.building_corps}`
        : null,
      created.apartments
        ? `${labels.premises.toLowerCase()} ${created.apartments}`
        : null,
      created.devices ? `устройств ${created.devices}` : null,
      created.devices_orphan
        ? `сирот ${created.devices_orphan}`
        : null,
      created.heat_radiators ? `УРТ ${created.heat_radiators}` : null,
      created.pulse_radio_modules ? `модулей ${created.pulse_radio_modules}` : null,
      created.radio_heads ? `накладок ${created.radio_heads}` : null,
      created.radio_head_bindings ? `привязок накладок ${created.radio_head_bindings}` : null,
    ].filter(Boolean);

    return (
      <Alert severity="success">
        <Typography variant="body2">
          Готово. Строк: {success.rows_processed}
          {typeof success.rows_skipped === "number" && success.rows_skipped > 0
            ? `, пропущено ${success.rows_skipped}`
            : ""}
          {bits.length ? `. Создано: ${bits.join(", ")}` : ""}
          {updatedTotal > 0 ? `. Обновлено: ${updatedTotal}` : ""}
        </Typography>
      </Alert>
    );
  }, [success, labels, isSettlementTenant]);

  const renderPreviewStatus = () => {
    if (previewLoading) {
      return (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 4 }}>
          <CircularProgress size={24} />
          <Typography variant="body2">
            Загрузка предварительного просмотра...
          </Typography>
        </Stack>
      );
    }

    if (!hasRows) {
      if (previewErrors.length > 0) {
        return (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <Alert severity="warning" sx={{ flexGrow: 1 }}>
              Файл разобран с ошибками. Откройте предпросмотр, чтобы посмотреть детали.
            </Alert>
            <Button variant="contained" onClick={handlePreviewOpen}>
              Открыть предпросмотр
            </Button>
          </Stack>
        );
      }

      return (
        <Typography variant="body2" color="text.secondary">
          Загрузите файл .xlsx или .csv, чтобы увидеть предварительный просмотр данных.
        </Typography>
      );
    }

    return (
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", sm: "center" }}
      >
        {/* <Typography variant="body2" sx={{ flexGrow: 1 }}>
          Загружено строк: {previewRows.length}. Откройте предпросмотр, чтобы
          отредактировать данные.
        </Typography> */}
            <Typography variant="body2" sx={{ flexGrow: 1 }}>
          {previewRangeText}. Откройте предпросмотр, чтобы редактировать.
        </Typography>
        <Button variant="contained" onClick={handlePreviewOpen}>
          Открыть предпросмотр
        </Button>
      </Stack>
    );
  };

  // const renderPreviewTable = () => (
  //   <Box
  //     sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}
  //   >
  //     <TableContainer sx={{ flex: 1, minHeight: 0 }}>
  //       <Table size="small" stickyHeader>
  //         <TableHead>
  //           <TableRow>
  //             <TableCell sx={{ width: 80 }}>#</TableCell>
  //             {COLUMN_CONFIG.map((column) => (
  //               <TableCell key={column.key}>
  //                 {column.label}
  //                 {column.required ? " *" : ""}
  //               </TableCell>
  //             ))}
  //             <TableCell sx={{ width: 80 }}>Действия</TableCell>
  //           </TableRow>
  //         </TableHead>
  //         <TableBody>
  //           {previewRows.map((row, rowIndex) => (
  //             <React.Fragment key={rowIndex}>
  //               <TableRow hover>
  //                 <TableCell>{row.row_index ?? rowIndex + 1}</TableCell>
  //                 {COLUMN_CONFIG.map((column) => (
  //                   <TableCell key={column.key}>
  //                     <TextField
  //                       value={getTextValue(row[column.key])}
  //                       onChange={(event) =>
  //                         handleRowChange(
  //                           rowIndex,
  //                           column.key,
  //                           event.target.value
  //                         )
  //                       }
  //                       size="small"
  //                       fullWidth
  //                     />
  //                   </TableCell>
  //                 ))}
  //                 <TableCell>
  //                   <IconButton
  //                     aria-label="Удалить строку"
  //                     color="error"
  //                     size="small"
  //                     onClick={() => handleRemoveRow(rowIndex)}
  //                   >
  //                     <DeleteOutlineRoundedIcon fontSize="small" />
  //                   </IconButton>
  //                 </TableCell>
  //               </TableRow>
  //               {row.row_errors && row.row_errors.length > 0 && (
  //                 <TableRow>
  //                   <TableCell
  //                     colSpan={COLUMN_CONFIG.length + 2}
  //                     sx={{ py: 1 }}
  //                   >
  //                     <Alert severity="warning" sx={{ m: 0 }}>
  //                       {row.row_errors.map((message) => (
  //                         <div key={message}>{message}</div>
  //                       ))}
  //                     </Alert>
  //                   </TableCell>
  //                 </TableRow>
  //               )}
  //             </React.Fragment>
  //           ))}
  //         </TableBody>
  //       </Table>
  //     </TableContainer>
  //   </Box>
  // );
  const handleRowUpdateCommit = useCallback((updatedRow) => {
    setPreviewRows((prev) =>
      prev.map((r) => (r.row_index === updatedRow.row_index ? updatedRow : r))
    );
  }, []);

  const dataGridLocaleText = useMemo(
    () => ({
      ...ruRU.components.MuiDataGrid.defaultProps.localeText,
      paginationDisplayedRows: ({ from, to, count }) =>
        `${from}–${to} из ${count !== -1 ? count : `более ${to}`}`,
      noRowsLabel:
        rowFilter === "errors"
          ? "Ошибочных строк нет"
          : rowFilter === "warnings"
          ? "Строк с предупреждениями нет"
          : "Нет строк",
    }),
    [rowFilter]
  );

  const renderPreviewTable = () => (
    <Box sx={{ flex: 1, display: "flex", minHeight: 0 }}>
      <DataGrid
        rows={previewRows}
        columns={gridColumns}
        getRowId={(row) => row.row_index}
        loading={previewLoading}
        disableColumnMenu
        density="compact"
        editMode="row"
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={(err) => console.error(err)}
        getRowClassName={getRowClassName}
        getCellClassName={(params) =>
          Array.isArray(params.row?.field_error_keys) &&
          params.row.field_error_keys.includes(params.field)
            ? "cell-has-error"
            : ""
        }
        pagination
        paginationMode={isFilteredView ? "client" : "server"}
        {...(isFilteredView ? {} : { rowCount: totalRows || 0 })}
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={(model) => {
          const nextPage = model.page;
          const nextSize = Math.min(model.pageSize ?? 100, 100);
          if (isFilteredView) {
            setPage(nextPage);
            setPageSize(nextSize);
            return;
          }
          loadPage(file, nextPage, nextSize);
        }}
        pageSizeOptions={[10, 50, 100]}
        columnHeaderHeight={36}
        rowHeight={54}
        sx={{ width: "100%" }}
        localeText={dataGridLocaleText}
      />
    </Box>
  );
  return (
    <Box
      sx={{
        p: 2,
        maxWidth: 1200,
        "& .row-has-errors": { bgcolor: "rgba(255, 0, 0, 0.06)" },
        "& .row-has-warnings": { bgcolor: "rgba(245, 158, 11, 0.08)" },
        "& .cell-has-error": {
          bgcolor: "rgba(244, 63, 94, 0.12)",
          color: "#8A1F11",
          fontWeight: 700,
        },
      }}
    >
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Массовый импорт объектов
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {isSettlementTenant ? (
            <>
              1. Скачайте шаблон и заполните данные посёлка, дома и приборов. Один дом
              можно повторить в нескольких строках, если в нём установлено несколько приборов.
            </>
          ) : (
            <>
              1. Скачайте шаблон и заполните обязательные поля: комплекс, дом, квартира.
              Корпус указывайте только если он реально есть; иначе оставьте пустым.
              Адреса вроде «36к1» и «36к2» лучше заводить отдельными домами.
              Если квартира пуста, а прибор указан — он попадёт в «Сироты» дома.
            </>
          )}
          <br />
          2. Загрузите подготовленный файл <strong>.xlsx</strong> или <strong>.csv</strong>, чтобы
          проверить данные и при необходимости исправить строки перед импортом.
          <br />
          3. Для прибора со встроенным радиомодулем используйте поле «Устройство DevEUI».
          Для отдельной накладки заполните её серийный номер и DevEUI.
          <br />
          4. Для многоканального импульсного модуля заполните блок «Регистратор»:
          ключ, номер канала и вес импульса.
          <br />
          5. Для УРТ дополнительно укажите параметры радиаторов и расчётный коэффициент.
        </Typography>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems="flex-start"
        >
          <Button
            variant="outlined"
            color="primary"
            startIcon={<CloudDownloadRoundedIcon />}
            onClick={handleDownloadTemplate}
          >
            Скачать шаблон
          </Button>
          {showExportAction && (
            <Button
              variant="contained"
              color="primary"
              startIcon={
                exporting ? <CircularProgress size={16} /> : <CloudDownloadRoundedIcon />
              }
              onClick={handleExportObjects}
              disabled={exporting}
            >
              {exporting ? "Формируем..." : "Выгрузить из системы"}
            </Button>
          )}
          {downloadMessage && (
            <Alert severity="success" sx={{ flex: 1 }}>
              {downloadMessage}
            </Alert>
          )}
        </Stack>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Stack spacing={3}>
          {/* Управление пагинацией до предпросмотра (опционально) */}
          {file && (
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems="center"
            >
              <Typography variant="body2" sx={{ flexGrow: 1 }}>
                Страница: {page + 1}
                {totalPages ? ` / ${totalPages}` : ""} · Размер страницы:
              </Typography>
              <TextField
                label="Строк на страницу"
                size="small"
                type="number"
                inputProps={{ min: 50, step: 50 }}
                value={pageSize}
                onChange={(e) => {
                  const nextSize = Math.max(
                    50,
                    parseInt(e.target.value || "50", 10)
                  );
                  setPageSize(nextSize);
                }}
                sx={{ width: 160 }}
              />
              <Button
                variant="outlined"
                onClick={() => loadPage(file, Math.max(page - 1, 0), pageSize)}
                disabled={isFilteredView || previewLoading || page <= 0}
              >
                Предыдущая
              </Button>
              <Button
                variant="outlined"
                onClick={() => loadPage(file, page + 1, pageSize)}
                disabled={
                  isFilteredView ||
                  previewLoading || (totalPages && page + 1 >= totalPages)
                }
              >
                Следующая
              </Button>
            </Stack>
          )}
          <Stack spacing={1}>
            <Typography variant="subtitle1">Загрузка файла</Typography>
            <Typography variant="body2" color="text.secondary">
              Выберите подготовленный файл <strong>.xlsx</strong> или <strong>.csv</strong>. После
              загрузки можно открыть предпросмотр и при необходимости
              исправить строки.
            </Typography>
          </Stack>

          <Button
            variant="outlined"
            component="label"
            startIcon={<DescriptionRoundedIcon />}
          >
            {file ? file.name : "Выбрать файл (.xlsx / .csv)"}
            <input
              hidden
              type="file"
              accept=".xlsx,.csv"
              onChange={handleFileChange}
            />
          </Button>

          <Divider />

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <Button
              variant="contained"
              color="primary"
              startIcon={
                uploading ? <CircularProgress size={16} /> : <CloudUploadRoundedIcon />
              }
              onClick={() => setConfirmUploadOpen(true)}
              disabled={!file || uploading || !isSpreadsheetSelected}
            >
              {uploading ? "Импортируем..." : "Загрузить напрямую"}
            </Button>
            {file && !isSpreadsheetSelected && (
              <Alert severity="warning" sx={{ flex: 1 }}>
                Формат файла не поддерживается. Сохраните таблицу в формате
                .xlsx или .csv.
              </Alert>
            )}
          </Stack>

          <FormControlLabel
            control={
              <Checkbox
                checked={skipInvalidRows}
                onChange={(event) => setSkipInvalidRows(event.target.checked)}
                size="small"
              />
            }
            label="Игнорировать строки с ошибками. Как администратор беру на себя ответственность за неполный импорт."
          />

          {error && <Alert severity="error">{error}</Alert>}

          {previewErrors.length > 0 && (
            <Alert severity="warning">
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Обнаружены замечания:
              </Typography>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {previewErrors.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </Alert>
          )}

          {renderPreviewStatus()}

          <Divider />

          {serverValidationErrors.length > 0 && (
            <Alert severity="warning">
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Ошибки при подтверждении импорта:
              </Typography>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {serverValidationErrors.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </Alert>
          )}

          {summaryInfo}

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "stretch", sm: "center" }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={importing ? <CircularProgress size={16} /> : <CloudUploadRoundedIcon />}
              onClick={() => setConfirmImportOpen(true)}
              disabled={!hasRows || importing}
            >
              {importing ? "Подтверждаем..." : "Подтвердить импорт"}
            </Button>
            <FormControlLabel
              control={
                <Checkbox
                  checked={skipInvalidRows}
                  onChange={(event) => setSkipInvalidRows(event.target.checked)}
                  size="small"
                />
              }
              label="Игнорировать строки с ошибками (ответственность администратора)"
            />
          </Stack>
        </Stack>
      </Paper>

      <Dialog
        fullScreen
        open={isPreviewModalOpen}
        onClose={handlePreviewClose}
        TransitionComponent={PreviewDialogTransition}
        PaperProps={{ sx: { display: "flex", flexDirection: "column" } }}
      >
        <AppBar
          position="relative"
          color="inherit"
          sx={{
            bgcolor: "background.paper",
            color: "text.primary",
            boxShadow: (theme) => theme.shadows[1],
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Toolbar
            sx={{
              gap: 2,
              flexWrap: { xs: "wrap", sm: "nowrap" },
              alignItems: "center",
            }}
          >
            <IconButton
              edge="start"
              color="inherit"
              onClick={handlePreviewClose}
              aria-label="Закрыть"
            >
              <CloseRoundedIcon />
            </IconButton>
            <Typography variant="h6" sx={{ flexGrow: 1, minWidth: 200 }}>
              Предпросмотр данных
            </Typography>
            {/* <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mr: { xs: 0, sm: 2 } }}
            >
              Строк: {previewRows.length}
            </Typography> */}
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mr: { xs: 0, sm: 2 } }}
            >
              {previewRangeText}
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={rowFilter === "errors"}
                  onChange={() => handleRowFilterChange("errors")}
                  size="small"
                />
              }
              label={`Ошибки${rowsWithErrorsCount ? `: ${rowsWithErrorsCount}` : ""}`}
              sx={{ mr: 1, whiteSpace: "nowrap" }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={rowFilter === "warnings"}
                  onChange={() => handleRowFilterChange("warnings")}
                  size="small"
                  color="warning"
                />
              }
              label={`Предупреждения${rowsWithWarningsCount ? `: ${rowsWithWarningsCount}` : ""}`}
              sx={{ mr: 1, whiteSpace: "nowrap" }}
            />
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                onClick={() => loadPage(file, Math.max(page - 1, 0), pageSize)}
                disabled={isFilteredView || previewLoading || !hasRows || page <= 0}
              >
                Предыдущая
              </Button>
              <Button
                variant="outlined"
                onClick={() => loadPage(file, page + 1, pageSize)}
                disabled={
                  isFilteredView ||
                  previewLoading ||
                  !hasRows ||
                  (totalPages && page + 1 >= totalPages)
                }
              >
                Следующая
              </Button>
            </Stack>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddRoundedIcon />}
              onClick={handleAddRow}
            >
              Добавить строку
            </Button>
          </Toolbar>
        </AppBar>
        <DialogContent
          sx={{ p: 0, flex: 1, display: "flex", flexDirection: "column" }}
        >
          {previewErrors.length > 0 && (
            <Alert severity="warning" sx={{ m: 2, mb: 0 }}>
              {previewErrors.join(" ")}
            </Alert>
          )}
          {renderPreviewTable()}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmUploadOpen}
        title="Загрузить файл без предпросмотра?"
        description={
          skipInvalidRows
            ? "Файл будет импортирован сразу. Строки с ошибками будут пропущены — вы как администратор берёте на себя ответственность за неполный импорт."
            : "Файл будет импортирован сразу. При любой ошибке в строках загрузка будет отклонена. Рекомендуем использовать предпросмотр."
        }
        confirmText="Загрузить"
        confirmColor="warning"
        onCancel={() => setConfirmUploadOpen(false)}
        onConfirm={() => {
          setConfirmUploadOpen(false);
          handleUpload();
        }}
      />

      <ConfirmDialog
        open={confirmImportOpen}
        title="Подтвердить импорт?"
        description={
          skipInvalidRows
            ? "Корректные строки будут импортированы, ошибочные — пропущены. Вы как администратор берёте на себя ответственность за неполный импорт. Отменить действие будет нельзя."
            : "Все строки будут импортированы в систему. При ошибках в строках импорт будет отклонён. Отменить действие будет нельзя."
        }
        confirmText="Импортировать"
        confirmColor="warning"
        onCancel={() => setConfirmImportOpen(false)}
        onConfirm={() => {
          setConfirmImportOpen(false);
          handleImport();
        }}
      />
    </Box>
  );
};

export default BulkImportPage;
